import axios from 'axios';
import { get, post } from '../../../../api/axios';
import { HomeEndpoints } from '../../../../api/endpoints/HomeEndpoints';
import { SetupGuideEndpoints } from '../../../../api/endpoints/SetupGuideEndpoints';
import { SystemEndpoints } from '../../../../api/endpoints/SystemEndpoints';
import { getProjectEnvVariables } from '../../../../shared/projectEnvVariables';
import type { SystemModulesDto } from '@/app/models/SystemModulesDto';
import type { SetupStepStateDto, AnnouncementDto, NewsDto, SystemModuleDto, ClientAppConfigDto } from './_models';

// Flat per-step progress (source of truth for "what is done"). The client
// assembles the visual guide from this + the catalog + the account's modules.
export const getSetupGuideState = () => get<SetupStepStateDto[]>(SetupGuideEndpoints.state);

// Dismisses the current guide: the given step codes are resolved as skipped, so
// the guide derives as hidden. Caller sends every step code of the current level.
export const dismissSetupGuide = (codes: string[]) => post<void>(SetupGuideEndpoints.dismiss, codes);

// Skips a single step. Non-skippable / unknown codes reject via the envelope
// (success:false) and surface as a SystemNotification error.
export const skipSetupStep = (code: string) => post<void>(SetupGuideEndpoints.stepSkip(code));

// Module entitlements (the `active` flag gates the fiscal level). Distinct from
// `getModules()` below, which feeds the home upsell cards.
export const getSystemModules = () => get<SystemModulesDto>(SystemEndpoints.modules);

export const getAnnouncements = () => {
  return get<AnnouncementDto[]>(HomeEndpoints.announcements);
};

export const getNews = () => {
  const { VITE_APP_NEWS_API_URL } = getProjectEnvVariables().envVariables;
  return axios.get<NewsDto[]>(VITE_APP_NEWS_API_URL).then((res) => res.data);
};

export const getModules = () => {
  return get<SystemModuleDto[]>(HomeEndpoints.modules);
};

// Per-tenant white-label market-app config that personalizes the home app card.
// Queried with meta.suppressGlobalError so a not-yet-shipped backend endpoint
// degrades silently to the generic upsell instead of raising an error toast.
export const getClientAppConfig = () => {
  return get<ClientAppConfigDto>(SystemEndpoints.clientApp);
};

export const dismissFirstAccess = () => {
  return post<void>(HomeEndpoints.dismissFirstAccess);
};

const CHANGELOG_URL = 'https://raw.githubusercontent.com/easypdverp/changelog/refs/heads/main/CHANGELOG.md';

export async function getChangelog(): Promise<string> {
  const response = await fetch(CHANGELOG_URL);
  if (!response.ok) throw new Error('Failed to fetch');
  return response.text();
}
